import React, { useContext, useEffect, useRef, useState } from "react";
import { getAuthenticatedHttpClient } from "@edx/frontend-platform/auth";
import { getUserDropDownData } from "../services/Userdropdown";
import { AppContext } from "@edx/frontend-platform/react";
import MainPopup from "./MainPopup";
import ConfirmEmailPopup from "./ConfirmEmailPopup";


function formatDateToCustom(inputDate) {
  if (inputDate === "N/A") {
    return "N/A";
  }
  const date = new Date(inputDate);

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

const viewCourseAccess = (courseData) =>
  courseData.enrollment.coursewareAccess.isStaff ||
  !(
    courseData.enrollment.coursewareAccess.hasUnmetPrerequisites ||
    courseData.enrollment.coursewareAccess.isTooEarly
  ) ||
  (courseData.enrollment.isAudit && courseData.enrollment.isAuditAccessExpired);

const CoursesPage = () => {
  const { authenticatedUser, config } = useContext(AppContext);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [courseStatus, setCourseStatus] = useState([]);
  const [sortOption, setSortOption] = useState("lastEnrolled");
  const [name, setName] = useState("");
  const [tags, setTags] = useState([]);
  const filterRef = useRef(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const coursesRef = useRef([]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const statusOption = {
    "In-Progress": 'progress',
    "Done": 'done'
  }
  const [courseProgress, setCourseProgress] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({});
  const httpClient = getAuthenticatedHttpClient();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleFilter = (e) => {
    e.stopPropagation();
    setIsFilterOpen(!isFilterOpen);
  };

  useEffect(() => {
    loadCourses(page, name.trim());
  }, [])

  const loadCourses = async (pageNumber, name, status, sort) => {
    try {
      if (loading || !hasMore) {
        return;
      }
      else {
        setLoading(true);
        const res = await getUserDropDownData(pageNumber, name, status, sort);
        setShowConfirmModal(res.data?.emailConfirmation?.isNeeded)
        const newCourses = res?.data?.courses?.map((courseData, index) => ({
          id: (pageNumber - 1) * 12 + index + 1,
          startDate: courseData?.courseRun?.startDate || "N/A",
          endDate: courseData?.courseRun?.endDate || "N/A",
          image: courseData.course.bannerImgSrc
            && `${config?.LMS_BASE_URL}${courseData.course.bannerImgSrc}`,
          title: courseData.course.courseName || "Untitled Course",
          codes: [courseData.courseRun.courseId.split(":")[1]],
          href: courseData.courseRun.homeUrl || "#",
          status: determineStatus(courseData),
          lastEnrolled: courseData.enrollment?.lastEnrolled || "N/A",
          courseAccess: viewCourseAccess(courseData),
          // courseProgress: courseData.progress,
          isStarted: new Date(courseData?.courseRun?.startDate) <= new Date(),
          courseId: courseData?.courseRun?.courseId,
        }));
        setFilteredCourses((prevCourses) => {
          const updatedCourses = [...prevCourses, ...newCourses];
          if (updatedCourses.length >= res?.data?.totalCourses) {
            setHasMore(false);
          }
          return updatedCourses;
        })
      }
    }
    catch (error) {
      console.error("Error loading courses:", error);
    }
    finally { 
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop + 100 >= document.documentElement.scrollHeight) {
        if (!loading && hasMore) {
          const searchName = tags?.length > 0 ? tags[0] : '';
          const status = courseStatus?.length > 0 ?  courseStatus.map((status) => statusOption[status]): '';
          const sort = sortOption !== 'lastEnrolled' && sortOption;
          setPage((prevPage) => {
            const nextPage = prevPage + 1;
            loadCourses(nextPage, searchName, status, sort);
            return nextPage;
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, tags, courseStatus, sortOption]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      {
        threshold: 0.5,
      }
    );
    coursesRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => {
      coursesRef.current.forEach((el) => {
        if (el) observer.unobserve(el);
      });
    };
  }, [filteredCourses]);

  const determineStatus = (courseData) => {
    if (courseData.courseRun.isArchived) {
      return "Done";
    }
    if (courseData.enrollment.hasStarted) {
      return "In-Progress";
    }
    return "Not Started";
  };

  const handleFilterChange = (status) => {
    let updatedStatus = [...courseStatus];
    if (updatedStatus.includes(status)) {
      updatedStatus = updatedStatus.filter((item) => item !== status);
    } else {
      updatedStatus.push(status);
    }
    let statusoption = updatedStatus.map((status) => statusOption[status]);
    setCourseStatus(updatedStatus);
    setPage(1);
    setHasMore(true);
    const searchName = tags?.length > 0 && tags[0] ;
    const sort = sortOption !== 'lastEnrolled' && sortOption;
    fetchCourses(1, searchName, statusoption && statusoption.length > 0 && statusoption, sort);
  };

  const handleSortChange = (option) => {
    setSortOption(option);
    setPage(1);
    setHasMore(true);
    const searchName = tags?.length > 0 && tags[0];
    const status = courseStatus?.length > 0 &&  courseStatus.map((status) => statusOption[status]);
    const sort = option !== 'lastEnrolled' && option;
    fetchCourses(1, searchName, status, sort);
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      if (name.trim() !== "" && tags.length === 0) {
        setTags([name.trim()]);
        setFilteredCourses([]);
        setPage(1);
        setHasMore(true);
        setName("");

        if (name.trim()) {
          const status = courseStatus?.length > 0 &&  courseStatus.map((status) => statusOption[status]);
          const sort = sortOption !==' lastEnrolled' && sortOption;
          fetchCourses(1, name && name.trim(), status, sort);
        }
      }
    }
  };

  const handleRemoveTag = () => {
    setTags([]);
    setName("");
    setFilteredCourses([]);
    setPage(1);
    setHasMore(true);
    const status = courseStatus?.length > 0 &&  courseStatus.map((status) => statusOption[status]);
    const sort = sortOption !== 'lastEnrolled' && sortOption;
    fetchCourses(1, '', status, sort);
  };

  const fetchCourses = async (page = 1, searchName, status, sort) => {
    const res = await getUserDropDownData(page, searchName, status, sort);
    const newCourses = mapCourseData(res?.data?.courses, (page - 1) * 12);
    setFilteredCourses(newCourses);
    if (newCourses.length >= res?.data?.totalCourses) {
      setHasMore(false);
    }
  };

  const mapCourseData = (courses, baseIndex = 0) =>
    courses.map((courseData, index) => ({
      id: baseIndex + index + 1,
      startDate: courseData?.courseRun?.startDate || "N/A",
      endDate: courseData?.courseRun?.endDate || "N/A",
      image: courseData.course.bannerImgSrc
        && `${config?.LMS_BASE_URL}${courseData.course.bannerImgSrc}`,
      title: courseData.course.courseName || "Untitled Course",
      codes: [courseData.courseRun.courseId.split(":")[1]],
      href: courseData.courseRun.homeUrl || "#",
      status: determineStatus(courseData),
      lastEnrolled: courseData.enrollment?.lastEnrolled || "N/A",
      courseAccess: viewCourseAccess(courseData),
      // courseProgress: courseData.progress,
      isStarted: new Date(courseData?.courseRun?.startDate) <= new Date(),
      courseId: courseData?.courseRun?.courseId,
    }));

  const handleProgressClick = async (e, courseId) => {
    e.preventDefault();
    e.stopPropagation();

    setLoadingProgress(prev => ({
      ...prev,
      [courseId]: true
    }));

    try {
      const response = await httpClient.get(
        `${config.LMS_BASE_URL}/api/user_progress/${courseId}`
      );

      setCourseProgress(prev => ({
        ...prev,
        [courseId]: response.data.progress
      }));

    } catch (error) {
      console.error("Error fetching progress", error);
    }
  };


  return (
    <>
    {showConfirmModal && <ConfirmEmailPopup showConfirmModal={showConfirmModal} setShowConfirmModal={setShowConfirmModal} />}
    <div className="container-fluid">
      <MainPopup />
      <div className="dashboard_subheader">
      <div className="header-row justify-content-between align-items-center">
        <h1 className="course_page_main_title">My Courses</h1>
        <div className="tag-input-container filterBlock">
          {authenticatedUser?.administrator && (
            <>
              <div className="viewing">
                <label>Viewing as:</label>
                <div className="tags">
                  {tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button type="button" onClick={handleRemoveTag}>
                        ✕
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={handleAddTag}
                    onBlur={handleAddTag}
                    placeholder={!(tags.length > 0) && "Add name"}
                    disabled={tags.length > 0}
                  />
                </div>
              </div>
            </>
          )}

          <div className="filter-section-wrapper" ref={filterRef}>
            <button
              className="filter-toggle-button"
              onClick={toggleFilter}
              aria-expanded={isFilterOpen}
            >
              {isFilterOpen ? <>Refine</> : <>Refine</>}
            </button>
            <div
              className={`filter-sort-section`}
              style={{
                display: isFilterOpen ? "block" : "none",
              }}
            >
              <div
                className="filter-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="filterSortSection">
                  <div className="filter-options">
                    <h3>Course Status</h3>
                    {["In-Progress", "Done"].map((status) => (
                      <label key={status}>
                        <input
                          type="checkbox"
                          checked={courseStatus.includes(status)}
                          onChange={() => handleFilterChange(status)}
                        />
                        {status}
                      </label>
                    ))}
                  </div>

                  <div className="sort-options">
                    <h3>Sort</h3>
                    <label>
                      <input
                        type="radio"
                        name="sort"
                        checked={sortOption === "lastEnrolled"}
                        onChange={() => handleSortChange("lastEnrolled")}
                      />
                      Last Enrolled
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="sort"
                        checked={sortOption === "title"}
                        onChange={() => handleSortChange("title")}
                      />
                      Title (A-Z)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div className="courses courses-grid">
        <div className="row">
        {filteredCourses?.map((course, index) => (
          <div
            key={course.id}
            ref={(el) => (coursesRef.current[index] = el)}
            className="col-sm-4 col-lg-3 fade-in"
          >
              <a
                href={course.courseAccess ? course.href : ""}
                onClick={(e) => {
                  if (!course.courseAccess) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="courses-block">
                  <div className="img">
                    <img src={course.image} className="course-image" alt={course.title} />
                  </div>
                  <div className="details">
                    <div>
                      <h2>{course.title}</h2>

                      <div className="course-info">
                        <span className="course-date">
                          {formatDateToCustom(course.startDate)} - {formatDateToCustom(course.endDate)}
                        </span>

                        <span className="course-progress">
                          {course.courseAccess && course.isStarted ? (
                            courseProgress[course.courseId] !== undefined ? (
                              courseProgress[course.courseId] === 0 ? (
                                <span className="start-course-text">
                                  Start your course
                                </span>
                              ) : (
                                <span className="progress-text">
                                  {courseProgress[course.courseId]}% Completed
                                </span>
                              )
                            ) : (
                              <button
                                className="progress-btn"
                                disabled={loadingProgress[course.courseId]}
                                onClick={(e) => handleProgressClick(e, course.courseId)}
                              >
                                {loadingProgress[course.courseId] ? "Loading..." : "Show Progress"}
                              </button>
                            )
                          ) : (
                            <span className="not-started-text">Not Started</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
};

export default CoursesPage;
